import RootStore from '@App/stores/root-store'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
    createCatalogTransfer,
    decideCatalogTransfer,
    getCatalogTransfer,
    linkCatalogTransferPublisher,
    submitCatalogTransfer,
    updateCatalogTransfer,
} from '@App/api/routes/rightsholder/catalog-transfer'
import { CatalogTransferReviewDecisionStatus, CatalogTransferView } from '@Entities/rightsholder/catalog-transfer-view'
import { CatalogTransferWizardStep } from '@Const/catalog-transfer-wizard-step'
import CatalogTransferImportStore from '@App/stores/rightsholder/catalogTransfer/catalog-transfer-import-store'

export interface CatalogTransferDTO {
    sourcePublisherIpIds?: string[]
    targetPublisherIpId?: string
    filesUploaded?: boolean
    effectiveDate?: Date
    writerNames?: string
    supplementalInfo?: string
    transferType?: 'full' | 'partial'
}

export interface SelectedPublisher {
    ipId: string
    name: string
    worksCount?: number
    hfaPublisherNumber?: string
}

export class CatalogTransferWizardStore {
    private rootStore: RootStore

    private _catalogTransferDTO: CatalogTransferDTO = {
        effectiveDate: undefined,
        filesUploaded: false,
        writerNames: undefined,
        supplementalInfo: undefined,
        sourcePublisherIpIds: [],
        targetPublisherIpId: undefined,
        transferType: undefined,
    }

    private _exportCurrentSearch: boolean | undefined = undefined
    private _currentCatalogTransfer: CatalogTransferView | undefined = undefined
    private _catalogTransferImportStore: CatalogTransferImportStore
    private _addtlInfoFormValid = false
    private _sourcePublishers: SelectedPublisher[] = []

    constructor(rootStore: RootStore) {
        makeObservable<
            CatalogTransferWizardStore,
            | '_catalogTransferDTO'
            | '_exportCurrentSearch'
            | '_currentCatalogTransfer'
            | '_addtlInfoFormValid'
            | '_sourcePublishers'
        >(this, {
            _catalogTransferDTO: observable,
            _exportCurrentSearch: observable,
            _currentCatalogTransfer: observable,
            _addtlInfoFormValid: observable,
            _sourcePublishers: observable,
            wizardStepIsValid: computed,
            isCatalogImported: computed,
            worksToTransferCount: computed,
            sourcePublishers: computed,
            updateCatalogTransferDTO: action,
            setExportCurrentSearch: action,
            setAddtlInfoFormValid: action,
            clearCurrentCatalogTransfer: action,
            resetStoreAfterWizard: action,
            addSourcePublisher: action,
            removeSourcePublisher: action,
        })
        this.rootStore = rootStore
        this._catalogTransferImportStore = new CatalogTransferImportStore(rootStore)
    }

    private invalidateCatalogTransferLists() {
        this.rootStore.catalogTransferListStore.invalidateCatalogTransfers()
    }

    get catalogTransferDTO(): Readonly<CatalogTransferDTO> {
        return this._catalogTransferDTO
    }

    get exportCurrentSearch(): boolean | undefined {
        return this._exportCurrentSearch
    }

    get isCatalogImported(): boolean {
        return this._catalogTransferImportStore.isCatalogImported
    }

    get importResult() {
        return this._catalogTransferImportStore.importResult
    }

    get isImportStreaming() {
        return this._catalogTransferImportStore.isImportStreaming
    }

    get importStreamStatus() {
        return this._catalogTransferImportStore.importStreamStatus
    }

    get importStreamProcessedCount() {
        return this._catalogTransferImportStore.importStreamProcessedCount
    }

    get importStreamErrorCount() {
        return this._catalogTransferImportStore.importStreamErrorCount
    }

    resetImport = () => {
        this._catalogTransferImportStore.resetImport()
    }

    setExportCurrentSearch(value: boolean) {
        this._exportCurrentSearch = value
    }

    get currentCatalogTransfer() {
        return this._currentCatalogTransfer
    }

    get sourcePublishers(): SelectedPublisher[] {
        return this._sourcePublishers
    }

    setAddtlInfoFormValid(isValid: boolean) {
        this._addtlInfoFormValid = isValid
    }

    async setCurrentCatalogTransfer(transferId: number) {
        let fetchedTransfer: CatalogTransferView | undefined
        const localCachedTransfer =
            this._currentCatalogTransfer?.id === transferId ? this._currentCatalogTransfer : undefined
        if (!localCachedTransfer) {
            const result = await getCatalogTransfer(this.rootStore.rightsholderStore.currentRightsholderId, transferId)
            fetchedTransfer = result.data
        }
        const currentTransfer = localCachedTransfer ?? fetchedTransfer
        if (!currentTransfer) {
            return
        }
        const seededSourcePublishers = currentTransfer?.sourcePublishers?.length
            ? currentTransfer.sourcePublishers
                  .filter((publisher) => publisher.ipId)
                  .map((publisher) => ({
                      ipId: publisher.ipId?.toString() ?? '',
                      name: publisher.publisherName,
                      worksCount: publisher.totalWorks ?? publisher.worksCount,
                      hfaPublisherNumber: publisher.hfaPublisherNumber,
                  }))
                  .filter((publisher) => publisher.ipId)
            : []
        runInAction(() => {
            this._currentCatalogTransfer = currentTransfer
            this._sourcePublishers = seededSourcePublishers
        })
        this.updateCatalogTransferDTO({
            sourcePublisherIpIds: seededSourcePublishers.map((publisher) => publisher.ipId),
            targetPublisherIpId: this._currentCatalogTransfer?.targetPublisher?.ipId?.toString(),
            filesUploaded: false,
            effectiveDate: this._currentCatalogTransfer?.effectiveDate
                ? new Date(this._currentCatalogTransfer?.effectiveDate)
                : undefined,
            writerNames: this._catalogTransferDTO?.writerNames,
            supplementalInfo: this._currentCatalogTransfer?.supplementalInformation,
            transferType: this._currentCatalogTransfer?.isPartialTransfer ? 'partial' : 'full',
        })
    }

    clearCurrentCatalogTransfer() {
        this._currentCatalogTransfer = undefined
    }

    get currentTransferId(): number {
        if (this._currentCatalogTransfer && this._currentCatalogTransfer.id) {
            return this._currentCatalogTransfer.id
        } else {
            throw new Error('Current Catalog Transfer ID Is Missing')
        }
    }

    updateCatalogTransferDTO(partial: Partial<CatalogTransferDTO>) {
        Object.assign(this._catalogTransferDTO, partial)
    }

    get wizardStepIsValid(): boolean {
        let isValid: boolean
        switch (this.rootStore.wizardFlowStore.currentIndex) {
            case CatalogTransferWizardStep.SelectTransferType:
                isValid = !!this._catalogTransferDTO?.transferType
                break
            case CatalogTransferWizardStep.SourcePublisher:
                isValid = this.acquiringStepValid
                break
            case CatalogTransferWizardStep.VerifyWorks:
                isValid = this._exportCurrentSearch !== undefined
                break
            case CatalogTransferWizardStep.ExportCatalog:
                isValid = this._catalogTransferImportStore.catalogExported
                break
            case CatalogTransferWizardStep.ImportCatalog:
                isValid = this.isCatalogImported
                break
            case CatalogTransferWizardStep.TargetPublisher:
                isValid = this.targetStepValid
                break
            case CatalogTransferWizardStep.UploadDocumentation:
                isValid = this._catalogTransferDTO.filesUploaded ?? false
                break
            case CatalogTransferWizardStep.ExtraInfo:
                isValid = this._addtlInfoFormValid
                break
            default:
                isValid = true
                break
        }
        return isValid
    }

    get acquiringStepValid(): boolean {
        return !!this._catalogTransferDTO?.sourcePublisherIpIds?.length
    }

    get targetStepValid(): boolean {
        return !!this._catalogTransferDTO?.targetPublisherIpId
    }

    get worksToTransferCount(): number {
        return (
            (this._exportCurrentSearch
                ? this.rootStore.transferCatalogSearchStore.worksRightsHolder?.count
                : this._currentCatalogTransfer?.sourcePublishers?.reduce(
                      (sum, publisher) => sum + (publisher.totalWorks ?? publisher.worksCount ?? 0),
                      0
                  )) ?? 0
        )
    }

    isSourcePublisherSelected(ipId?: string | null): boolean {
        if (!ipId) {
            return false
        }
        return this._sourcePublishers.some((publisher) => publisher.ipId === ipId)
    }

    addSourcePublisher(publisher: SelectedPublisher) {
        if (this.isSourcePublisherSelected(publisher.ipId)) {
            return
        }
        this._sourcePublishers = [...this._sourcePublishers, publisher]
        this.updateCatalogTransferDTO({
            sourcePublisherIpIds: this._sourcePublishers.map((item) => item.ipId),
        })
    }

    removeSourcePublisher(ipId: string) {
        this._sourcePublishers = this._sourcePublishers.filter((publisher) => publisher.ipId !== ipId)
        this.updateCatalogTransferDTO({
            sourcePublisherIpIds: this._sourcePublishers.map((item) => item.ipId),
        })
    }

    async createCatalogTransfer(catalogTransferType: 'ACQUISITION' | 'RELINQUISHMENT') {
        try {
            const result = await createCatalogTransfer(this.rootStore.rightsholderStore.currentRightsholderId, {
                catalogTransferType: catalogTransferType,
            })
            runInAction(() => {
                this.resetStoreAfterWizard()
                this._currentCatalogTransfer = result.data
            })
            this.invalidateCatalogTransferLists()
            return true
        } catch {
            return false
        }
    }

    async updateCatalogTransfer() {
        try {
            if (!this._currentCatalogTransfer?.id) {
                return false
            }
            const result = await updateCatalogTransfer(
                this.rootStore.rightsholderStore.currentRightsholderId,
                this._currentCatalogTransfer.id,
                {
                    catalogTransferType: 'ACQUISITION',
                    isPartialTransfer: this.catalogTransferDTO.transferType === 'partial',
                    effectiveDate: this._catalogTransferDTO.effectiveDate,
                    writerNames: this._catalogTransferDTO.writerNames,
                    supplementalInformation: this._catalogTransferDTO.supplementalInfo,
                    targetPublisherIpId: this._catalogTransferDTO.targetPublisherIpId
                        ? Number(this._catalogTransferDTO.targetPublisherIpId)
                        : undefined,
                }
            )
            runInAction(() => {
                this._currentCatalogTransfer = result.data
            })
            this.invalidateCatalogTransferLists()
            return true
        } catch {
            return false
        }
    }

    async submitCatalogTransfer() {
        if (!this._currentCatalogTransfer?.id) {
            throw new Error('No current catalog transfer to submit')
        }
        const result = await submitCatalogTransfer(
            this.rootStore.rightsholderStore.currentRightsholderId,
            this._currentCatalogTransfer.id
        )
        runInAction(() => {
            this._currentCatalogTransfer = result.data
        })
        this.invalidateCatalogTransferLists()
        this.rootStore.popupStore.setAlertData('Your catalog transfer has been submitted successfully.', false)
    }

    async submitReviewDecision(decision: CatalogTransferReviewDecisionStatus, notes?: string) {
        if (!this._currentCatalogTransfer?.id) {
            throw new Error('No current catalog transfer to review')
        }
        this.rootStore.preloaderStore.openTransparentPreloader(true)
        try {
            const result = await decideCatalogTransfer(
                this.rootStore.rightsholderStore.currentRightsholderId,
                this._currentCatalogTransfer.id,
                decision,
                notes
            )
            runInAction(() => {
                this._currentCatalogTransfer = result.data
            })
            this.invalidateCatalogTransferLists()
            this.rootStore.popupStore.setAlertData(
                decision === 'APPROVE' ? 'Transfer accepted.' : 'Transfer denied.',
                false
            )
            return true
        } catch {
            this.rootStore.popupStore.setAlertData('Unable to update catalog transfer decision.', true)
            return false
        } finally {
            this.rootStore.preloaderStore.openTransparentPreloader(false)
        }
    }

    async linkSourcePublisher() {
        if (!this._currentCatalogTransfer?.id) {
            throw new Error('No current catalog transfer to link source publisher')
        }
        const sourcePublisherIpIds = this.catalogTransferDTO.sourcePublisherIpIds
        if (!sourcePublisherIpIds?.length) {
            throw new Error('No source publisher selected')
        }
        try {
            await linkCatalogTransferPublisher(
                this.rootStore.rightsholderStore.currentRightsholderId,
                this._currentCatalogTransfer.id,
                sourcePublisherIpIds.map((ipId) => Number(ipId))
            )

            const result = await getCatalogTransfer(
                this.rootStore.rightsholderStore.currentRightsholderId,
                this._currentCatalogTransfer.id
            )
            runInAction(() => (this._currentCatalogTransfer = result.data))
            this.invalidateCatalogTransferLists()

            return true
        } catch {
            return false
        }
    }

    async importWorksFromExcel() {
        await this._catalogTransferImportStore.importWorksFromExcel()
    }

    async importWorksFromExcelStream() {
        await this._catalogTransferImportStore.importWorksFromExcelStream()
    }

    async exportWorksToExcel() {
        await this._catalogTransferImportStore.exportWorksToExcel()
    }

    async onDocumentsUploaded() {
        if (!this._currentCatalogTransfer?.id) {
            throw new Error('No current catalog transfer to update documentation')
        }

        const result = await getCatalogTransfer(
            this.rootStore.rightsholderStore.currentRightsholderId,
            this._currentCatalogTransfer.id
        )

        runInAction(() => {
            this._currentCatalogTransfer = result.data
        })
        this.updateCatalogTransferDTO({ filesUploaded: true })
    }

    resetStoreAfterWizard() {
        this._catalogTransferDTO = {
            effectiveDate: undefined,
            filesUploaded: false,
            supplementalInfo: undefined,
            sourcePublisherIpIds: [],
            targetPublisherIpId: undefined,
            transferType: undefined,
        }
        this._exportCurrentSearch = undefined
        this._catalogTransferImportStore.resetStoreAfterWizard()
        this._currentCatalogTransfer = undefined
        this._addtlInfoFormValid = false
        this._sourcePublishers = []
        this.rootStore.transferCatalogSearchStore.resetStore()
    }
}

export default CatalogTransferWizardStore
